import { useMutation, useQuery, useQueryClient } from 'react-query';
import { BreakoutService } from '../api/BreakoutService';
import { Breakout, TypicalBreakout } from '../api/protosCompiled/breakout/breakout_pb';

// Declare the global preventAutoCreate property
declare global {
  interface Window {
    __preventAutoCreateBreakout?: boolean;
  }
}

// Global flag to prevent auto-creation of breakouts
// Make it truly global so it persists between rerenders
window.__preventAutoCreateBreakout = window.__preventAutoCreateBreakout || false;

// Export function to control the auto-creation behavior
export function setPreventAutoCreate(value: boolean) {
  window.__preventAutoCreateBreakout = value;
}

// Helper to check the current state
function isAutoCreatePrevented() {
  return window.__preventAutoCreateBreakout === true;
}

// Fetch hooks
export function useFetchEstimateBreakouts(id: string) {
  return useQuery(['estimateBreakouts', id], () => BreakoutService.getEstimateBreakouts(id), {
    enabled: !!id,
    refetchOnWindowFocus: false,
    onSuccess: data => {
      data.breakoutsList.sort((a, b) => a.breakoutname.localeCompare(b.breakoutname));
      // Only create default breakout if not prevented and no breakouts exist
      if (data.breakoutsList.length === 0 && !isAutoCreatePrevented()) {
        const newBreakout = new Breakout();
        newBreakout.setTakeoffid(id);
        newBreakout.setBreakoutname('Generic Breakout');
        newBreakout.setMultiplier(1);
        BreakoutService.createEstimateBreakout(newBreakout);
      }
    },
  });
}

export function useFetchChangeOrderBreakouts(id: string) {
  return useQuery(['changeOrderBreakouts', id], () => BreakoutService.getChangeOrderBreakouts(id), {
    enabled: !!id,
    refetchOnWindowFocus: false,
    onSuccess: data => {
      data.breakoutsList.sort((a, b) => a.breakoutname.localeCompare(b.breakoutname));
      // Only create default breakout if not prevented and no breakouts exist
      if (data.breakoutsList.length === 0 && !isAutoCreatePrevented()) {
        const newBreakout = new Breakout();
        newBreakout.setTakeoffid(id);
        newBreakout.setBreakoutname('Generic Breakout');
        newBreakout.setMultiplier(1);
        BreakoutService.createChangeOrderBreakout(newBreakout);
      }
    },
  });
}

// Mutation hooks for estimate breakouts
export function useCreateEstimateBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation((data: Breakout) => BreakoutService.createEstimateBreakout(data), {
    onSuccess: () => {
      // Set preventAutoCreate to true before invalidating queries
      setPreventAutoCreate(true);
      queryClient.invalidateQueries('estimateBreakouts');

      // Also invalidate countedAssemblies to refresh takeoff data
      queryClient.invalidateQueries('countedAssemblies');

      // Reset the flag after a longer delay to be safer
      setTimeout(() => {
        setPreventAutoCreate(false);
      }, 2000);
    },
  });
}

export function useUpdateEstimateBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation((data: Breakout) => BreakoutService.updateEstimateBreakout(data), {
    onSuccess: () => {
      // Set preventAutoCreate to true before invalidating queries
      setPreventAutoCreate(true);
      queryClient.invalidateQueries('estimateBreakouts');

      // Also invalidate countedAssemblies to refresh takeoff data
      queryClient.invalidateQueries('countedAssemblies');

      // Reset the flag after a longer delay to be safer
      setTimeout(() => {
        setPreventAutoCreate(false);
      }, 2000);
    },
  });
}

export function useDeleteEstimateBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation((id: string) => BreakoutService.deleteEstimateBreakout(id), {
    onSuccess: () => {
      // Set preventAutoCreate to true before invalidating queries
      setPreventAutoCreate(true);
      queryClient.invalidateQueries('estimateBreakouts');

      // Also invalidate countedAssemblies to refresh takeoff data
      queryClient.invalidateQueries('countedAssemblies');

      // Reset the flag after a longer delay
      setTimeout(() => {
        setPreventAutoCreate(false);
      }, 2000);
    },
  });
}

// Mutation hooks for change order breakouts
export function useCreateChangeOrderBreakoutMutation() {
  const queryClient = useQueryClient();

  return useMutation((data: Breakout) => BreakoutService.createChangeOrderBreakout(data), {
    onSuccess: () => {
      // Set preventAutoCreate to true before invalidating queries
      setPreventAutoCreate(true);
      queryClient.invalidateQueries('changeOrderBreakouts');

      // Also invalidate countedAssemblies to refresh takeoff data
      queryClient.invalidateQueries('countedAssemblies');

      // Reset the flag after a longer delay to be safer
      setTimeout(() => {
        setPreventAutoCreate(false);
      }, 2000);
    },
  });
}

export function useUpdateChangeOrderBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation((data: Breakout) => BreakoutService.updateChangeOrderBreakout(data), {
    onSuccess: () => {
      // Set preventAutoCreate to true before invalidating queries
      setPreventAutoCreate(true);
      queryClient.invalidateQueries('changeOrderBreakouts');

      // Also invalidate countedAssemblies to refresh takeoff data
      queryClient.invalidateQueries('countedAssemblies');

      // Reset the flag after a longer delay to be safer
      setTimeout(() => {
        setPreventAutoCreate(false);
      }, 2000);
    },
  });
}

export function useDeleteChangeOrderBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation((id: string) => BreakoutService.deleteChangeOrderBreakout(id), {
    onSuccess: () => {
      // Set preventAutoCreate to true before invalidating queries
      setPreventAutoCreate(true);
      queryClient.invalidateQueries('changeOrderBreakouts');

      // Also invalidate countedAssemblies to refresh takeoff data
      queryClient.invalidateQueries('countedAssemblies');

      // Reset the flag after a longer delay
      setTimeout(() => {
        setPreventAutoCreate(false);
      }, 2000);
    },
  });
}

export function useCreateOrUpdateTypicalEstimateBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    (data: TypicalBreakout) => BreakoutService.createOrUpdateTypicalEstimateBreakout(data),
    {
      onSuccess: () => {
        // Invalidate all breakout-related queries
        queryClient.invalidateQueries('estimateBreakouts');
        queryClient.invalidateQueries(['estimateBreakouts']);

        // Also invalidate countedAssemblies to refresh takeoff data
        queryClient.invalidateQueries('countedAssemblies');
        queryClient.invalidateQueries(['countedAssemblies']);

        // Force immediate refetch of all breakout queries
        queryClient.refetchQueries('estimateBreakouts');
      },
    }
  );
}

export function useCreateOrUpdateTypicalChangeOrderBreakoutMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    (data: TypicalBreakout) => BreakoutService.createOrUpdateTypicalChangeOrderBreakout(data),
    {
      onSuccess: () => {
        // Invalidate all breakout-related queries
        queryClient.invalidateQueries('changeOrderBreakouts');
        queryClient.invalidateQueries(['changeOrderBreakouts']);

        // Also invalidate countedAssemblies to refresh takeoff data
        queryClient.invalidateQueries('countedAssemblies');
        queryClient.invalidateQueries(['countedAssemblies']);

        // Force immediate refetch of all breakout queries
        queryClient.refetchQueries('changeOrderBreakouts');
      },
    }
  );
}
